import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    ValidatorFn,
} from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AppStateFacade } from '@app-state';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import { ISettingsScalability } from '@models';

import { BehaviorSubject } from 'rxjs';
import {
    delay,
    takeUntil,
} from 'rxjs/operators';

enum ScalabilityControl {
    signalR = 'signalR',
    signalRConfig = 'signalRConfig',
}

enum ScalabilitySignalRControl {
    webSocketTransport = 'webSocketTransport',
    serverSentEventsTransport = 'serverSentEventsTransport',
    longPollingTransport = 'longPollingTransport',
}

interface FormValue {
    signalR: boolean;
    signalRConfig: {
        webSocketTransport: boolean;
        serverSentEventsTransport: boolean;
        longPollingTransport: boolean;
    };
}

@Component({
    selector: 'ui-scalability-settings',
    templateUrl: './ui-scalability-settings.component.html',
    styleUrls: ['../ui-setting-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiScalabilityComponent extends UiSettingForm implements OnInit {

    public ScalabilityControl = ScalabilityControl;
    public ScalabilitySignalRControl = ScalabilitySignalRControl;

    public signalRChange$!: BehaviorSubject<boolean>;

    constructor(
        private _fb: FormBuilder,
        cd: ChangeDetectorRef,
        appState: AppStateFacade,
    ) {
        super(
            cd,
            appState,
        );
    }

    ngOnInit() {
        this.signalRChange$ = new BehaviorSubject(this.settings.scalability.signalR.enabled);

        this.formGroup = this._fb.group({
            [ScalabilityControl.signalR]: [
                this.settings.scalability.signalR.enabled,
            ],
        });

        const signalRSettingsFormGroup = this._fb.group({
            [ScalabilitySignalRControl.webSocketTransport]: [
                this.settings.scalability.signalR.transportProtocol.webSocketTransport,
            ],
            [ScalabilitySignalRControl.serverSentEventsTransport]: [
                this.settings.scalability.signalR.transportProtocol.serverSentEventsTransport,
            ],
            [ScalabilitySignalRControl.longPollingTransport]: [
                this.settings.scalability.signalR.transportProtocol.longPollingTransport,
            ],
        });

        this.formGroup.addControl(ScalabilityControl.signalRConfig, signalRSettingsFormGroup);

        this.signalRChange$
            .pipe(
                delay(0),
                takeUntil(this._destroyed$),
            )
            .subscribe(enabled => {
                const signalRConfigFormGroup = this.formGroup.get(ScalabilityControl.signalRConfig)!;

                if (enabled) {
                    signalRConfigFormGroup.enable();
                    signalRConfigFormGroup.setValidators(this._transportOptionsValidator);
                    signalRConfigFormGroup.updateValueAndValidity();
                } else {
                    signalRConfigFormGroup.disable();
                    signalRConfigFormGroup.setValidators(null);
                }
            });

        this.formGroup.valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                Object.assign(
                    this.settings.scalability,
                    this._formValueToPayload(this.formGroup.getRawValue()),
                );
            });

        this.isReadOnly$
            .pipe(
                delay(0),
                takeUntil(this._destroyed$),
            )
            .subscribe(
                isReadOnly => {
                    if (isReadOnly) {
                        this.formGroup.disable();
                    } else {
                        this.formGroup.enable();
                        // needed to disable the signalR transport settings if the case
                        this.signalRChange$.next(this.signalRChange$.getValue());
                    }
                },
            );
    }

    public onSignalRToggle(event: MatSlideToggleChange) {
        this.signalRChange$.next(event.checked);
    }

    private _formValueToPayload = (formValue: FormValue): ISettingsScalability => ({
        signalR: {
            enabled: formValue.signalR,
            transportProtocol: {
                webSocketTransport: formValue.signalRConfig.webSocketTransport,
                serverSentEventsTransport: formValue.signalRConfig.serverSentEventsTransport,
                longPollingTransport: formValue.signalRConfig.longPollingTransport,
            },
        },
    })

    private _transportOptionsValidator: ValidatorFn = (form: AbstractControl) => {
        if (
            form.get(ScalabilitySignalRControl.webSocketTransport)!.value ||
            form.get(ScalabilitySignalRControl.serverSentEventsTransport)!.value ||
            form.get(ScalabilitySignalRControl.longPollingTransport)!.value
        ) {
            return null;
        }

        return {
            invalidSelection: true,
        };
    }
}
